import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * ThemeToggle component - Theme toggle button
 * @param {string} size - Button size: 'sm' | 'md' | 'lg'
 * @param {boolean} showLabel - Whether to show text label
 * @param {string} className - Additional CSS classes
 */
const ThemeToggle = ({ 
  size = 'md', 
  showLabel = false,
  className = '' 
}) => {
  const { isDark, toggleTheme, theme } = useTheme();

  // Set icon and button size based on size prop
  const sizeClasses = {
    sm: {
      button: 'p-1.5',
      icon: 'w-4 h-4',
      text: 'text-xs',
    },
    md: {
      button: 'p-2',
      icon: 'w-5 h-5',
      text: 'text-sm',
    },
    lg: {
      button: 'p-2.5',
      icon: 'w-6 h-6',
      text: 'text-base',
    },
  };

  const currentSize = sizeClasses[size] || sizeClasses.md;

  return (
    <button
      onClick={toggleTheme}
      className={`
        ${currentSize.button}
        rounded-lg
        transition-all duration-200
        flex items-center gap-2
        ${className}
      `}
      style={{
        color: 'var(--color-text-secondary)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        <Sun className={`${currentSize.icon}`} style={{ color: 'var(--color-warning)' }} />
      ) : (
        <Moon className={`${currentSize.icon}`} />
      )}
      {showLabel && (
        <span className={currentSize.text}>
          {theme.label}
        </span>
      )}
    </button>
  );
};

/**
 * ThemeSwitch component - Switch-style theme toggle
 */
export const ThemeSwitch = ({ className = '' }) => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`
        relative w-14 h-7 rounded-full
        transition-colors duration-300
        ${className}
      `}
      style={{
        backgroundColor: isDark ? 'var(--color-primary)' : 'var(--color-border)',
      }}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {/* Slider */}
      <span
        className={`
          absolute top-0.5 left-0.5
          w-6 h-6 rounded-full
          bg-white shadow-md
          transition-transform duration-300
          flex items-center justify-center
          ${isDark ? 'translate-x-7' : 'translate-x-0'}
        `}
      >
        {isDark ? (
          <Moon className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
        ) : (
          <Sun className="w-4 h-4" style={{ color: 'var(--color-warning)' }} />
        )}
      </span>
    </button>
  );
};

export default ThemeToggle;
